import './App.css'

import { NotificationBell } from '@reader-tracker/shared/components/NotificationBell'
import ErrorPage from '@reader-tracker/shared/core/Error'
import { AdminLayout } from '@reader-tracker/shared/layouts/AdminLayout'
import { MainLayout } from '@reader-tracker/shared/layouts/Main'
import { UserCallbackPage } from '@reader-tracker/shared/pages/auth/Callback'
import { LoginPage } from '@reader-tracker/shared/pages/auth/LoginPage'
import { LogsProvider } from '@reader-tracker/shared/providers/Logs'
import { useAuthListener } from '@reader-tracker/shared/stores/authStore'
import { Navigate, RouterProvider, createBrowserRouter } from 'react-router-dom'
import { APP_NAME, NAV_ITEMS } from './navbar.config'
import AboutPage from './pages/About/About'
import Home from './pages/Home/Home'
import LibraryPage from './pages/Library/Library'
import ReaderPage from './pages/Reader/Reader'
import SearchPage from './pages/Search/Search'
import TitleDetailPage from './pages/TitleDetail/TitleDetail'
import AppSettingsPage from './pages/admin/app-settings/AppSettingsPage'
import ChaptersPage from './pages/admin/chapters/ChaptersPage'
import EdgeFunctionsPage from './pages/admin/edge-functions/EdgeFunctionsPage'
import MangaFormPage from './pages/admin/manga/MangaFormPage'
import MangaListPage from './pages/admin/manga/MangaListPage'
import SourcesPage from './pages/admin/sources/SourcesPage'
import TitleSourcesPage from './pages/admin/title-sources/TitleSourcesPage'

const readerLayout = (children: React.ReactNode) => (
  <MainLayout
    appName={APP_NAME}
    navItems={NAV_ITEMS}
    rightSlot={<NotificationBell />}
  >
    {children}
  </MainLayout>
)

const router = createBrowserRouter([
  {
    path: '/',
    element: readerLayout(<Home />),
    errorElement: <ErrorPage />,
  },
  {
    path: '/about',
    element: readerLayout(<AboutPage />),
    errorElement: <ErrorPage />,
  },

  // ── Manga Tracker ─────────────────────────────────────────
  {
    path: '/library',
    element: readerLayout(<LibraryPage />),
    errorElement: <ErrorPage />,
  },
  {
    path: '/search',
    element: readerLayout(<SearchPage />),
    errorElement: <ErrorPage />,
  },
  {
    path: '/title/:id',
    element: readerLayout(<TitleDetailPage />),
    errorElement: <ErrorPage />,
  },
  {
    // Reader uses its own full-screen layout — no MainLayout wrapper
    path: '/read/:chapterId',
    element: <ReaderPage />,
    errorElement: <ErrorPage />,
  },

  // ── Auth ─────────────────────────────────────────────────
  {
    path: '/auth/login',
    element: readerLayout(<LoginPage />),
    errorElement: <ErrorPage />,
  },
  {
    path: '/auth/callback',
    element: readerLayout(<UserCallbackPage />),
    errorElement: <ErrorPage />,
  },

  // ── Admin ────────────────────────────────────────────────
  {
    path: '/admin',
    element: <Navigate to="/admin/app-settings" replace />,
  },
  {
    path: '/admin/app-settings',
    element: (
      <AdminLayout appName={APP_NAME}>
        <AppSettingsPage />
      </AdminLayout>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/admin/manga',
    element: (
      <AdminLayout appName={APP_NAME}>
        <MangaListPage />
      </AdminLayout>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/admin/manga/new',
    element: (
      <AdminLayout appName={APP_NAME}>
        <MangaFormPage />
      </AdminLayout>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/admin/manga/:id/edit',
    element: (
      <AdminLayout appName={APP_NAME}>
        <MangaFormPage />
      </AdminLayout>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/admin/sources',
    element: (
      <AdminLayout appName={APP_NAME}>
        <SourcesPage />
      </AdminLayout>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/admin/manga/:id/chapters',
    element: (
      <AdminLayout appName={APP_NAME}>
        <ChaptersPage />
      </AdminLayout>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/admin/manga/:id/sources',
    element: (
      <AdminLayout appName={APP_NAME}>
        <TitleSourcesPage />
      </AdminLayout>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/admin/edge-functions',
    element: (
      <AdminLayout appName={APP_NAME}>
        <EdgeFunctionsPage />
      </AdminLayout>
    ),
    errorElement: <ErrorPage />,
  },
])

function App() {
  useAuthListener()
  return (
    <LogsProvider>
      <RouterProvider router={router} />
    </LogsProvider>
  )
}

export default App
